import { MEMBER_ROLES, type InviteDoc, type MemberDoc, type MemberRole } from '@sonder/shared'
import { useState, type FormEvent, type ReactNode } from 'react'
import { useCallable, useDoc } from '@/data/hooks'
import { ActivityLine, Button, Field, Input, Modal, Select, StatusPill, Textarea, useConfirm, useToast, type ActivityTimes } from '@/ui'

/**
 * Who can sign in to one client's portal. Shared by the client's own People page and the staff
 * console's access page, so both describe roles the same way. A role is portal access only: it says
 * what the person can do here, not what their job is. The job goes in `title`.
 */

export type Member = MemberDoc & { id: string }
export type Invite = InviteDoc & { id: string }

const ROLE_OPTIONS = (Object.keys(MEMBER_ROLES) as MemberRole[]).map((value) => ({ value, label: MEMBER_ROLES[value].label }))

export function RolePill({ role }: { role: MemberRole }) {
  return <StatusPill tone={role === 'owner' ? 'ink' : 'neutral'} dot={false}>{MEMBER_ROLES[role].label}</StatusPill>
}

/** The two roles, spelled out. Shown once above any list or form that uses them. */
export function RoleLegend({ className }: { className?: string }) {
  return (
    <dl className={className}>
      {(Object.keys(MEMBER_ROLES) as MemberRole[]).map((r) => (
        <div key={r} className="flex items-baseline gap-3 py-1 text-small">
          <dt className="w-28 shrink-0 font-medium text-ink">{MEMBER_ROLES[r].label}</dt>
          <dd className="text-dim">{MEMBER_ROLES[r].hint}</dd>
        </div>
      ))}
    </dl>
  )
}

export function shortDate(ms: number) {
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

type InviteInput = { clientId: string; email: string; role: MemberRole; name?: string; title?: string; note?: string }

/** Every action on a client's members and invites, each a Cloud Function so the change is audited. */
export function useMemberActions(clientId: string) {
  const { toast } = useToast()
  const createInvite = useCallable<InviteInput, { inviteId: string }>('createInvite')
  const revokeInvite = useCallable<{ inviteId: string }, { ok: true }>('revokeInvite')
  const removeMember = useCallable<{ clientId: string; uid: string }, { ok: true }>('removeMember')
  const setMemberRole = useCallable<{ clientId: string; uid: string; role: MemberRole }, { ok: true }>('setMemberRole')
  const updateMember = useCallable<{ clientId: string; uid: string; name: string; title: string }, { ok: true }>('updateMember')
  const confirm = useConfirm()
  const [busy, setBusy] = useState<string | null>(null)

  const run = async (key: string, fn: () => Promise<unknown>, done: string) => {
    setBusy(key)
    try {
      await fn()
      toast(done, 'success')
      return true
    } catch (e) {
      toast((e as Error).message?.replace(/^.*?:\s*/, '') || 'Something went wrong', 'error')
      return false
    } finally {
      setBusy(null)
    }
  }

  return {
    busy,
    invite: (input: Omit<InviteInput, 'clientId'>) => run(`invite:${input.email}`, () => createInvite({ clientId, ...input }), `Invitation sent to ${input.email}`),
    resend: (i: Invite) => run(`resend:${i.id}`, () => createInvite({ clientId, email: i.email, role: i.role, name: i.name || undefined, title: i.title || undefined }), `Invitation re-sent to ${i.email}`),
    revoke: (i: Invite) => run(`revoke:${i.id}`, () => revokeInvite({ inviteId: i.id }), `Invitation to ${i.email} cancelled`),
    remove: async (m: Member) => {
      const ok = await confirm({ title: `Remove ${m.name || m.email}?`, body: 'They will no longer be able to sign in to this portal. Everything they filled in stays. You can invite them again later.', confirmLabel: 'Remove access', cancelLabel: 'Keep access' })
      return ok ? run(`remove:${m.id}`, () => removeMember({ clientId, uid: m.id }), `${m.name || m.email} no longer has access`) : false
    },
    update: (m: Member, fields: { name: string; title: string }) => run(`update:${m.id}`, () => updateMember({ clientId, uid: m.id, ...fields }), 'Saved'),
    setRole: (m: Member, role: MemberRole) => run(`role:${m.id}`, () => setMemberRole({ clientId, uid: m.id, role }), `${m.name || m.email} is now a ${MEMBER_ROLES[role].label.toLowerCase()}`),
  }
}

export type MemberActions = ReturnType<typeof useMemberActions>

export function InviteForm({ actions, compact }: { actions: MemberActions; compact?: boolean }) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [title, setTitle] = useState('')
  const [note, setNote] = useState('')
  const [role, setRole] = useState<MemberRole>('collaborator')
  const sending = actions.busy?.startsWith('invite:')

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (await actions.invite({ email: email.trim(), role, name: name.trim() || undefined, title: title.trim() || undefined, note: note.trim() || undefined })) {
      setEmail('')
      setName('')
      setTitle('')
      setNote('')
      setRole('collaborator')
    }
  }

  return (
    <form onSubmit={onSubmit} className="grid gap-5">
      <Field label="Email">
        <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" />
      </Field>
      <div className={compact ? 'grid gap-5' : 'grid gap-5 sm:grid-cols-2'}>
        <Field label="Name" optional>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Job title" optional>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Director of Marketing" />
        </Field>
      </div>
      <Field label="Portal access" hint={MEMBER_ROLES[role].hint}>
        <Select<MemberRole> value={role} onValueChange={setRole} options={ROLE_OPTIONS} />
      </Field>
      <Field label="Note" optional hint="A line from you, included in the invitation email.">
        <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Looking forward to working together. Start with the brand profiles when you have a minute." />
      </Field>
      <div>
        <Button type="submit" disabled={Boolean(sending) || !email}>{sending ? 'Sending…' : 'Send invite'}</Button>
      </div>
    </form>
  )
}

/**
 * One person with access. `manage` swaps the role pill for a select and adds edit and remove; `selfId` hides remove on yourself.
 * `showActivity` adds when they were last active: staff only, since only staff can read another person's user record.
 */
export function MemberRow({ member, actions, manage, selfId, showJoined, showActivity }: { member: Member; actions?: MemberActions; manage?: boolean; selfId?: string | null; showJoined?: boolean; showActivity?: boolean }) {
  const [editing, setEditing] = useState(false)
  const activity = useDoc<ActivityTimes>(showActivity ? `users/${member.id}` : null)
  const isSelf = member.id === selfId
  const busy = actions?.busy
  return (
    <li className={ROW}>
      <span className={ROW_TEXT}>
        <span className="block text-body text-ink">
          {member.name || member.email}
          {member.title && <span className="text-dim">, {member.title}</span>}
          {isSelf && <span className="ml-2 whitespace-nowrap text-small text-dim">(you)</span>}
        </span>
        {/* Email, then dates, each on its own line so the address doesn't get lost among them. */}
        {member.name && <span className="block break-words text-small text-dim">{member.email}</span>}
        {showJoined && <span className="block text-small text-dim">Joined {shortDate(member.joinedAt)}</span>}
        {showActivity && !activity.loading && <ActivityLine times={activity.data} className="block" />}
      </span>
      {manage && actions ? (
        <span className="flex items-center gap-4">
          <Select<MemberRole>
            size="sm"
            value={member.role}
            onValueChange={(role) => void actions.setRole(member, role)}
            options={ROLE_OPTIONS}
            disabled={busy === `role:${member.id}`}
            aria-label={`Role for ${member.name || member.email}`}
            className="w-40"
          />
          <Button variant="link" size="sm" arrow={false} onClick={() => setEditing(true)}>Edit</Button>
          {!isSelf && (
            <Button variant="danger-link" size="sm" arrow={false} disabled={busy === `remove:${member.id}`} onClick={() => void actions.remove(member)}>
              {busy === `remove:${member.id}` ? 'Removing…' : 'Remove'}
            </Button>
          )}
        </span>
      ) : (
        <RolePill role={member.role} />
      )}
      {manage && actions && editing && <EditMemberModal member={member} actions={actions} onClose={() => setEditing(false)} />}
    </li>
  )
}

function EditMemberModal({ member, actions, onClose }: { member: Member; actions: MemberActions; onClose: () => void }) {
  const [name, setName] = useState(member.name)
  const [title, setTitle] = useState(member.title ?? '')
  const saving = actions.busy === `update:${member.id}`
  const formId = `edit-member-${member.id}`
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (await actions.update(member, { name: name.trim(), title: title.trim() })) onClose()
  }
  return (
    <Modal
      open
      onClose={onClose}
      title={member.name || member.email}
      footer={
        <>
          <Button variant="ghost" arrow={false} onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} arrow={false} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </>
      }
    >
      <form id={formId} onSubmit={onSubmit} className="grid gap-5">
        <p className="text-small text-dim">{member.email}</p>
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Job title" optional>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Director of Marketing" />
        </Field>
      </form>
    </Modal>
  )
}

export function InviteRow({ invite, actions }: { invite: Invite; actions: MemberActions }) {
  const expired = invite.expiresAt < Date.now()
  const busy = actions.busy
  return (
    <li className={ROW}>
      <span className={ROW_TEXT}>
        <span className="block text-body text-ink">
          {invite.name || invite.email}
          {invite.title && <span className="text-dim">, {invite.title}</span>}
        </span>
        {invite.name && <span className="block break-words text-small text-dim">{invite.email}</span>}
        <span className="block text-small text-dim">
          {expired ? `Invite expired ${shortDate(invite.expiresAt)}` : `Invite sent ${shortDate(invite.createdAt)} · expires ${shortDate(invite.expiresAt)}`}
        </span>
      </span>
      {/* These rows sit under "Invited, not yet signed in", so only an expired invite needs a tag. */}
      <RolePill role={invite.role} />
      {expired && <StatusPill tone="danger">Expired</StatusPill>}
      <span className="flex items-center gap-4">
        <Button variant="link" size="sm" arrow={false} disabled={busy === `resend:${invite.id}`} onClick={() => void actions.resend(invite)}>
          {busy === `resend:${invite.id}` ? 'Sending…' : 'Resend'}
        </Button>
        <Button variant="danger-link" size="sm" arrow={false} disabled={busy === `revoke:${invite.id}`} onClick={() => void actions.revoke(invite)}>
          {busy === `revoke:${invite.id}` ? 'Cancelling…' : 'Cancel'}
        </Button>
      </span>
    </li>
  )
}

/** A person: details take the row's full width on a phone, with the controls on a line beneath; from sm up the controls sit to the right. */
const ROW = 'flex flex-wrap items-center gap-x-4 gap-y-3 py-4'
const ROW_TEXT = 'grid min-w-0 basis-full gap-0.5 sm:min-w-[14rem] sm:flex-1 sm:basis-auto'

export function MemberList({ children }: { children: ReactNode }) {
  return <ul className="divide-y divide-gray-200 border-y border-gray-200">{children}</ul>
}

export function sortMembers<T extends { role: MemberRole; name: string; email: string }>(items: T[]) {
  return [...items].sort((a, b) => (a.role === b.role ? (a.name || a.email).localeCompare(b.name || b.email) : a.role === 'owner' ? -1 : 1))
}
